import { useCallback, useEffect, useRef, useState } from 'react';
import type { BookPage, ExploreItem } from '../../content/expand';
import '../../styles/book-reader.css';

interface Props { item: ExploreItem; progress: number; onProgress: (value:number)=>void; onComplete: ()=>void }
type Turn = { from:number; to:number; direction:'forward'|'backward' };

function Caption({page}:{page:BookPage}) {
  if(!page.pinyin)return <p>{page.text}</p>;
  const syllables=page.pinyin?.trim().split(/\s+/)??[];
  let index=0;
  return <p aria-label={page.text}>{Array.from(page.text).map((char,i)=>/\p{Script=Han}/u.test(char)
    ? <ruby key={i}>{char}<rt>{syllables[index++]??''}</rt></ruby>
    : <span className="book-punctuation" key={i}>{char}</span>)}</p>;
}

export function BookExperience({item,progress,onProgress,onComplete}:Props) {
  const pages=item.pages?.length?item.pages:[{image:item.image,text:item.subtitle}];
  const [page,setPage]=useState(()=>progress>=1?0:Math.max(0,Math.min(pages.length-1,Math.ceil(progress*pages.length)-1)));
  const [turn,setTurn]=useState<Turn|null>(null);
  const turning=useRef<Turn|null>(null);
  const [auto,setAuto]=useState(false),[narrating,setNarrating]=useState(false);
  const [audioError,setAudioError]=useState(false);
  const audioRef=useRef<HTMLAudioElement>(null);
  const callbacks=useRef({onProgress,onComplete});
  useEffect(()=>{callbacks.current={onProgress,onComplete};},[onProgress,onComplete]);
  const commit=useCallback((target:number)=>{
    setPage(target);callbacks.current.onProgress((target+1)/pages.length);
    if(target===pages.length-1){setAuto(false);callbacks.current.onComplete();}
  },[pages.length]);
  const finish=useCallback(()=>{
    const pending=turning.current;if(!pending)return;
    turning.current=null;setTurn(null);commit(pending.to);
  },[commit]);
  const go=useCallback((next:number)=>{
    if(turning.current)return;
    const target=Math.max(0,Math.min(pages.length-1,next));if(target===page)return;
    audioRef.current?.pause();
    if(window.matchMedia('(prefers-reduced-motion: reduce)').matches){commit(target);return;}
    const pending:Turn={from:page,to:target,direction:target>page?'forward':'backward'};
    turning.current=pending;setTurn(pending);
  },[page,pages.length,commit]);
  useEffect(()=>{
    if(!turn)return;
    // Also settles if animationend is lost when the tab is backgrounded.
    const timer=window.setTimeout(finish,900);return()=>clearTimeout(timer);
  },[turn,finish]);
  useEffect(()=>{
    if(!auto||turn||narrating)return;
    if(page===pages.length-1){setAuto(false);return;}
    const timer=window.setTimeout(()=>go(page+1),5000);return()=>clearTimeout(timer);
  },[auto,page,turn,narrating,go,pages.length]);
  useEffect(()=>{
    for(const target of [page-1,page+1])if(pages[target]){const image=new Image();image.src=pages[target].image;}
  },[page,item]);
  useEffect(()=>{
    const audio=audioRef.current;if(!audio)return;
    audio.pause();
    if(!narrating||turn)return;
    const src=pages[page].narrationUrl;
    if(!src){setNarrating(false);setAuto(false);setAudioError(true);return;}
    setAudioError(false);audio.src=src;let cancelled=false;
    void audio.play().catch(()=>{if(!cancelled){setNarrating(false);setAuto(false);setAudioError(true);}});
    return()=>{cancelled=true;audio.pause();};
  },[narrating,page,turn,item]);
  const surface=(index:number)=><article className="book-page-surface" lang={item.language??'zh'}><img src={pages[index].image} alt={`第 ${index+1} 页插画`} draggable={false}/><div className="book-caption"><small>{index+1} / {pages.length}{pages[index].sourcePage?` · 原书 ${pages[index].sourcePage}`:''}</small><Caption page={pages[index]}/></div></article>;
  const gesture=useRef<{x:number;y:number}|null>(null);
  return <div className={`book-reader immersive-book-reader ${item.readingLayout==='original'?'book-original':''}`}>
    <header><div><small>云朵绘本馆 · {item.narratorLabel??'01-少女精品讲述'}</small><h1>{item.title}</h1></div><div><button className={narrating?'is-active':''} aria-pressed={narrating} onClick={()=>{if(narrating)setAuto(false);setNarrating(value=>!value);}}>♫ {narrating?'暂停讲述':'语音讲述'}</button><button className={auto?'is-active':''} aria-pressed={auto} disabled={page===pages.length-1} onClick={()=>{setAuto(!auto);if(item.readingLayout==='original')setNarrating(!auto);}}>↻ {auto?'停止自动翻阅':'自动翻阅'}</button></div></header>
    <section className="book-spread" aria-label="绘本阅读区">
      <button aria-label="上一页" onClick={()=>go(page-1)} disabled={page===0||!!turn}>‹</button>
      <div className={`book-stage ${turn?'is-turning turn-'+turn.direction:''}`} tabIndex={0} aria-label="绘本，左右滑动或按方向键翻页" aria-busy={!!turn}
        onKeyDown={event=>{if(event.key==='ArrowLeft'||event.key==='ArrowRight'){event.preventDefault();go(page+(event.key==='ArrowLeft'?-1:1));}}}
        onPointerDown={event=>{if(event.pointerType!=='mouse'&&event.isPrimary)gesture.current={x:event.clientX,y:event.clientY};}}
        onPointerCancel={()=>{gesture.current=null;}}
        onPointerUp={event=>{const start=gesture.current;gesture.current=null;if(!start)return;const dx=event.clientX-start.x,dy=event.clientY-start.y;if(Math.abs(dx)>50&&Math.abs(dx)>Math.abs(dy)*1.3)go(page+(dx<0?1:-1));}}>
        {surface(turn?.to??page)}
        {turn&&<><div className="book-stationary-half" aria-hidden="true">{surface(turn.from)}</div><div className="book-turning-leaf" aria-hidden="true" onAnimationEnd={event=>{if(event.target===event.currentTarget)finish();}}><div className="book-leaf-front">{surface(turn.from)}</div><div className="book-leaf-back">{surface(turn.to)}</div></div></>}
      </div>
      <button aria-label="下一页" onClick={()=>go(page+1)} disabled={page===pages.length-1||!!turn}>›</button>
    </section>
    <footer><div>{pages.map((_,index)=><button key={index} className={index===page?'is-active':''} aria-current={index===page?'page':undefined} disabled={!!turn} onClick={()=>go(index)} aria-label={`第 ${index+1} 页`}/>)}</div><span role="status">{audioError?'语音暂时无法播放，请稍后重试。':`第 ${page+1} / ${pages.length} 页 · 左右滑动，翻开下一页故事`}</span>{item.attribution&&<details className="book-source"><summary>版权与来源</summary><div><p>{item.attribution}</p>{item.creditsImage&&<img src={item.creditsImage} alt="原书版权页" loading="lazy"/>}</div></details>}</footer>
    <audio ref={audioRef} preload="metadata" onEnded={()=>{if(auto&&page<pages.length-1)go(page+1);else setNarrating(false);}} onError={()=>{setNarrating(false);setAuto(false);setAudioError(true);}}/>
  </div>;
}
